import { useEffect, useRef, useState } from 'preact/hooks';
import { APP_NAME, APP_NAME_AR } from '../data/brand';
import { FIRST_ADDRESS, MoveError, applyMove, formatCode, openMove, splitCode } from '../lib/move';

// Moving in from the app's old address (src/lib/move.ts): by link, a screen of its own while everything comes across.
// The welcome's "Moving from the old address?" asks the old address for it; on an iPhone's Home Screen, the move code is
// typed or pasted there instead. Either way the app then starts afresh with what was saved (or, where Ahillah was already
// in use here, with the old nights added).

const fresh = () => location.replace('/');
const iosDevice = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
/** An app on an iPhone's or iPad's Home Screen, which keeps its own storage apart from Safari's. */
const homeScreenApp = () => (navigator as unknown as { standalone?: boolean }).standalone === true;
/** Safari on an iPhone or iPad, not the Home Screen app. */
const iosBrowser = () => iosDevice() && !homeScreenApp();

type Failed = MoveError['why'];
const WHY: Record<Failed, { title: string; text: string }> = {
  code: { title: 'This link has run out', text: 'A move link works for 30 days. Open the old address again for a new one, or start afresh here.' },
  network: { title: 'Ahillah could not be reached', text: 'Check your connection and try again.' },
  old: { title: 'This browser is too old for the move', text: 'Update it and try again, or save your nights as a file at the old address and restore them in Settings.' },
  storage: { title: 'Your things could not be saved here', text: 'Try again. If it happens again, save your nights as a file at the old address and restore them in Settings.' },
};
/** A move that can be tried again as it is: the connection, or the phone's storage, may be better a moment later. */
const retry = (state: string) => state === 'network' || state === 'storage';

/** Arriving by link from the old address: the saved things come across, then the app starts with them. */
export function MoveIn({ id, moveKey }: { id: string; moveKey: string }) {
  const [state, setState] = useState<'working' | 'all' | 'nights' | Failed>('working');
  const [tries, setTries] = useState(0);
  const [copied, setCopied] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    setState('working');
    openMove(id, moveKey)
      .then((p) => setState(applyMove(p)))
      .catch((e) => setState(e instanceof MoveError ? e.why : 'network'));
  }, [tries]);
  // Each new state starts at its heading, so a screen reader says what happened.
  useEffect(() => heading.current?.focus({ preventScroll: true }), [state]);
  const code = formatCode(id, moveKey);
  // Moved from the old app as installed on this device (?app): its icon stays behind, still opening the old address.
  const fromApp = new URLSearchParams(location.search).has('app');
  const failed = state !== 'working' && state !== 'all' && state !== 'nights' ? WHY[state] : null;
  return (
    <div class="onboard move-in" role="dialog" aria-modal="true" aria-labelledby="move-title">
      <div class="onboard-card">
        <div class="brand" aria-hidden="true">
          <span>{APP_NAME}</span>
          <span class="arabic-script" lang="ar" dir="rtl">{APP_NAME_AR}</span>
        </div>
        {state === 'working' && (
          <>
            <h1 id="move-title" ref={heading} tabIndex={-1}>Bringing your things across…</h1>
            <p class="lead">Your saved nights, your place, reminders and settings.</p>
          </>
        )}
        {(state === 'all' || state === 'nights') && (
          <>
            <h1 id="move-title" ref={heading} tabIndex={-1}>{state === 'all' ? 'Welcome to Ahillah’s new address' : 'Your old nights are here too'}</h1>
            <p class="lead">
              {state === 'all'
                ? 'Everything you saved is here.'
                : 'The nights you saved at the old address are added to these. Everything else here stays as it was.'}
            </p>
            {iosBrowser() && (
              <div class="move-again">
                <p>Adding Ahillah to your Home Screen? It keeps its own storage, so open it there, tap <b>Moving from the old address?</b> and enter this code:</p>
                <p class="move-shown-code">{code}</p>
                <button class="btn-ghost" onClick={() => navigator.clipboard?.writeText(code).then(() => setCopied(true)).catch(() => {})}>
                  {copied ? 'Copied' : 'Copy the code'}
                </button>
              </div>
            )}
            {fromApp && (
              <div class="move-again">
                <p>
                  <b>Install Ahillah again.</b> Its old icon on this device still opens the old address. Open ahillah.pages.dev in your
                  browser and install it as you did before (in the browser’s menu: <b>Install app</b>, or <b>Add to Home screen</b>).
                  Then remove the old icon.
                </p>
              </div>
            )}
            <button class="btn big" onClick={fresh}>Continue</button>
          </>
        )}
        {failed && (
          <>
            <h1 id="move-title" ref={heading} tabIndex={-1}>{failed.title}</h1>
            <p class="lead">{failed.text}</p>
            {retry(state) && <button class="btn big" onClick={() => setTries((t) => t + 1)}>Try again</button>}
            <button class={retry(state) ? 'btn-ghost' : 'btn big'} onClick={fresh}>Start afresh</button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * In the welcome: "Moving from the old address?" opens the old address, which sends everything saved there straight
 * back here. With nothing saved there, it sends the person back with ?bring, and this says so and offers the move code.
 * An app on an iPhone's Home Screen cannot reach its old icon's storage, so it takes the code from the start.
 */
export function MoveCode() {
  const [back] = useState(() => new URLSearchParams(location.search).has('bring'));
  const [open, setOpen] = useState(back);
  const [code, setCode] = useState('');
  const [state, setState] = useState<'idle' | 'working' | Failed>('idle');
  const box = useRef<HTMLDivElement>(null);
  const canPaste = typeof navigator !== 'undefined' && !!navigator.clipboard?.readText;
  // Opened, or with something to say, the field comes into view (a small phone with large text puts it below the fold).
  useEffect(() => box.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), [open, state]);
  // Said once: a reload starts the welcome as usual.
  useEffect(() => {
    if (!back) return;
    const q = new URLSearchParams(location.search);
    q.delete('bring');
    history.replaceState(null, '', location.pathname + (q.toString() ? `?${q}` : ''));
  }, []);
  const go = async () => {
    const c = splitCode(code);
    if (!c) return setState('code');
    setState('working');
    try {
      applyMove(await openMove(c.id, c.key));
      fresh();
    } catch (e) {
      setState(e instanceof MoveError ? e.why : 'network');
    }
  };
  if (!open) {
    return (
      <button class="link-btn" onClick={() => (homeScreenApp() ? setOpen(true) : location.assign(`${FIRST_ADDRESS}/?bring`))}>
        Moving from the old address?
      </button>
    );
  }
  return (
    <div class="move-code" ref={box}>
      {back ? (
        <p class="fine" role="alert">
          Nothing was saved at the old address in this browser.{' '}
          {iosDevice()
            ? 'If you used Ahillah from an icon on your Home Screen, open that icon, tap Get my move code, and enter the code here.'
            : 'If you used Ahillah in another browser, open it there as before, and your things will come across. Or enter a move code here.'}
        </p>
      ) : (
        <p class="move-hint">Open the app’s old icon on your Home Screen and tap <b>Get my move code</b>.</p>
      )}
      <label for="move-code">Your move code, from the old address</label>
      <div class="move-row">
        <input id="move-code" value={code} placeholder="XXXX-XXXX-XXXX-XXXX" autoComplete="off" autoCapitalize="characters" spellcheck={false}
          onInput={(e) => setCode((e.currentTarget as HTMLInputElement).value)} onKeyDown={(e) => e.key === 'Enter' && void go()} />
        {canPaste && (
          <button class="btn-ghost small" onClick={async () => setCode(await navigator.clipboard.readText().catch(() => code))}>Paste</button>
        )}
      </div>
      <button class="btn-ghost move-go" onClick={() => void go()} disabled={state === 'working'}>{state === 'working' ? 'Bringing your things across…' : 'Bring my things across'}</button>
      {state === 'code' && <p class="fine" role="alert">That code does not open anything. Check it, or get a new one at the old address.</p>}
      {state === 'network' && <p class="fine" role="alert">Ahillah could not be reached. Check your connection and try again.</p>}
      {state === 'old' && <p class="fine" role="alert">{WHY.old.text}</p>}
      {state === 'storage' && <p class="fine" role="alert">{WHY.storage.title}. {WHY.storage.text}</p>}
    </div>
  );
}
